// Вкладка «Пользователи» админ-консоли: список, форма редактирования,
// доступные категории, профиль аватара.
// Выделено из admin.ts (аудит, этап 2 — декомпозиция).
import { state } from '../shared/state';
import { $, showToast } from '../shared/index';
import { formatAdminBalanceSummary, isUserBlocked, readAdminBillingPayload, setAdminBillingFields } from '../billing/admin-billing';
import type { AppUser, CategoryData } from '../shared/types';
import { getUserUiModules } from '../shared/ui-modules';
import {
  type ApiErrorBody,
  formatApiErrors,
  getAdminCategories,
} from './admin-shared';
import { buildApiKeyRow, type ApiKeyView } from '../api-keys';

const USERNAME_RE = /^[a-zA-Z0-9_-]+$/;

export interface AdminUserRecord extends AppUser {
  expiration_date?: string;
  is_blocked?: boolean | number;
  dialog_avatar_id?: string | null;
  /** Движок живого хода Диалога: true — граф, false — FSM, null — серверный флаг. */
  dialog_og_turn?: boolean | null;
  /** Деактивация с отложенной чисткой (см. retention worker): пользователь
   * заблокирован и будет удалён совсем в purge_scheduled_at. */
  deactivated_at?: number | string | null;
  purge_scheduled_at?: number | string | null;
}

function toTimestamp(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const ms = Number(value);
  return Number.isFinite(ms) && ms > 0 ? ms : null;
}

function formatPurgeDate(value: number | string | null | undefined): string {
  const ms = toTimestamp(value);
  if (ms === null) return '';
  return new Date(ms).toLocaleDateString('ru-RU');
}

let adminUsersCache: Record<string, AdminUserRecord> = {};

function getPasswordErrors(password: string) {
  const errors: string[] = [];
  if (password.length < 8) errors.push('Пароль должен содержать не менее 8 символов');
  if (password.length > 128) errors.push('Пароль должен содержать не более 128 символов');
  if (!/[A-Z]/.test(password)) errors.push('Пароль должен содержать хотя бы одну заглавную букву');
  if (!/[a-z]/.test(password)) errors.push('Пароль должен содержать хотя бы одну строчную букву');
  if (!/[0-9]/.test(password)) errors.push('Пароль должен содержать хотя бы одну цифру');
  if (!/[\W_]/.test(password)) errors.push('Пароль должен содержать хотя бы один специальный символ');
  return errors;
}

// Блок персональных токенных лимитов пользователя удалён целиком: полей
// admin-input-context-limit / admin-output-generation-limit нет в index.html с
// тех пор, как из users убрали колонки input_context_limit и
// output_generation_limit (миграция 025). Функции продолжали жить, читая
// несуществующие элементы, — админ мог решить, что настройка есть.
// Лимиты токенов настраиваются в категории (вкладка «Категории»).

function renderAllowedCategories(cats: Record<string, CategoryData>, selected: string[] = [], selectFirstWhenEmpty = false) {
  const container = $('admin-allowed-categories-container');
  if (!container) return;
  container.textContent = '';

  const names = Object.keys(cats || {}).sort((a, b) => a.localeCompare(b));
  if (names.length === 0) {
    container.textContent = 'Категории не найдены';
    return;
  }

  names.forEach(cname => {
    const lbl = document.createElement('label');
    lbl.style.display = 'flex';
    lbl.style.alignItems = 'center';
    lbl.style.gap = '8px';
    lbl.style.cursor = 'pointer';

    const chk = document.createElement('input');
    chk.type = 'checkbox';
    chk.value = cname;
    chk.className = 'admin-allowed-cat-chk';
    chk.checked = selected.includes(cname) || (selectFirstWhenEmpty && selected.length === 0 && cname === names[0]);

    lbl.appendChild(chk);
    lbl.appendChild(document.createTextNode(cname));
    container.appendChild(lbl);
  });
}

function getCheckedAllowedCategories() {
  return Array.from(document.querySelectorAll<HTMLInputElement>('.admin-allowed-cat-chk:checked')).map(chk => chk.value);
}

function populateDefaultCategorySelect(selectedCategory = '') {
  const select = $<HTMLSelectElement>('admin-default-category');
  if (!select) return;

  const previousValue = selectedCategory || select.value;
  const allowed = getCheckedAllowedCategories();
  select.textContent = '';

  allowed.forEach(cname => {
    const opt = document.createElement('option');
    opt.value = cname;
    opt.textContent = cname;
    select.appendChild(opt);
  });

  if (allowed.includes(previousValue)) {
    select.value = previousValue;
  } else if (allowed.length > 0) {
    select.value = allowed[0];
  }
}

function bindAllowedCategorySync(selectedCategory = '') {
  populateDefaultCategorySelect(selectedCategory);
  document.querySelectorAll('.admin-allowed-cat-chk').forEach(chk => {
    chk.addEventListener('change', () => {
      populateDefaultCategorySelect();
    });
  });
}

export async function loadAdminUsers() {
  const r = await fetch('/api/admin/users', { headers: { 'Authorization': 'Bearer ' + state.authToken } });
  if (r.ok) {
    const users = await r.json() as Record<string, AdminUserRecord>;
    adminUsersCache = users;
    const list = $('admin-user-list');
    if (!list) return;
    list.textContent = '';

    Object.entries(users).forEach(([username, u]) => {
      const isExpired = u.expiration_date && new Date(u.expiration_date) < new Date();
      const blocked = isUserBlocked(u);
      const deactivated = toTimestamp(u.deactivated_at) !== null;
      const purgeDate = formatPurgeDate(u.purge_scheduled_at);
      let statusClass: string;
      let statusLabel: string;
      if (deactivated) {
        // «Удалён» кнопкой корзины — на самом деле деактивирован до purge.
        // Пишем это прямо, иначе админ считает пользователя удалённым.
        statusClass = 'status-badge--expired';
        statusLabel = purgeDate ? `🕒 Деактивирован, удаление ${purgeDate}` : '🕒 Деактивирован';
      } else if (blocked) {
        statusClass = 'status-badge--expired';
        statusLabel = '🔒 Заблокирован';
      } else if (isExpired) {
        statusClass = 'status-badge--expired';
        statusLabel = 'Истек';
      } else {
        statusClass = 'status-badge--active';
        statusLabel = 'Активен';
      }

      const billingInfo = formatAdminBalanceSummary(u);

      const el = document.createElement('div');
      el.className = 'user-item';
      el.innerHTML = `
        <div class="user-item-info">
          <div class="flex-center-gap">
            <span class="user-item-name">${DOMPurify.sanitize(username)}</span>
            <span class="status-badge ${statusClass}">${statusLabel}</span>
          </div>
          <span class="user-item-cat">${DOMPurify.sanitize(u.email || 'E-mail не задан')} | ${DOMPurify.sanitize(u.category || '')} | ${billingInfo}</span>
          ${u.expiration_date ? `<span class="user-expiration">Срок до: ${DOMPurify.sanitize(u.expiration_date)}</span>` : ''}
        </div>
        <div class="user-item-actions">
          <button class="nav-btn btn-action-sm edit-usr-btn" data-usr="${DOMPurify.sanitize(username)}" aria-label="Редактировать ${DOMPurify.sanitize(username)}">✏️</button>
          ${deactivated ? '' : `<button class="nav-btn btn-action-sm--danger del-usr-btn" data-usr="${DOMPurify.sanitize(username)}" title="Деактивировать: вход закрывается сразу, полное удаление — через grace-период" aria-label="Деактивировать ${DOMPurify.sanitize(username)}">🗑️</button>`}
          <button class="nav-btn btn-action-sm--danger purge-usr-btn" data-usr="${DOMPurify.sanitize(username)}" title="Удалить окончательно — без возможности восстановления" aria-label="Удалить окончательно ${DOMPurify.sanitize(username)}">⛔</button>
        </div>
      `;
      list.appendChild(el);
    });

    document.querySelectorAll('.edit-usr-btn').forEach(b => b.addEventListener('click', (e: Event) => {
      const btn = (e.target as Element).closest('button') as HTMLButtonElement;
      editAdminUser(btn.dataset.usr || '', users);
    }));
    document.querySelectorAll('.del-usr-btn').forEach(b => b.addEventListener('click', (e: Event) => {
      const btn = (e.target as Element).closest('button') as HTMLButtonElement;
      deleteAdminUser(btn.dataset.usr || '');
    }));
    document.querySelectorAll('.purge-usr-btn').forEach(b => b.addEventListener('click', (e: Event) => {
      const btn = (e.target as Element).closest('button') as HTMLButtonElement;
      purgeAdminUser(btn.dataset.usr || '');
    }));
  }
}

type AdminAvatarProfile = {
  avatar_id: string;
  label?: string;
};

const escAttr = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Профили аватара для карточки пользователя (кэш на время открытой формы —
 * список доступных и селект назначенного строятся из одного ответа). */
let adminAvatarProfilesCache: AdminAvatarProfile[] = [];

async function fetchAdminAvatarProfiles(): Promise<AdminAvatarProfile[]> {
  try {
    const r = await fetch('/api/admin/avg-dialog/profiles', {
      headers: { Authorization: 'Bearer ' + state.authToken },
    });
    if (r.ok) return await r.json() as AdminAvatarProfile[];
  } catch {
    return [];
  }
  return [];
}

/** «Доступные профили Диалога» — по схеме «Доступных категорий»: чекбоксы,
 * пустой набор = доступны все. */
function renderAllowedAvatars(profiles: AdminAvatarProfile[], selected: string[] = []): void {
  const container = $('admin-allowed-avatars-container');
  if (!container) return;
  container.textContent = '';

  if (!profiles.length) {
    container.textContent = 'Профили аватаров не найдены';
    return;
  }

  profiles.forEach((p) => {
    const lbl = document.createElement('label');
    lbl.style.display = 'flex';
    lbl.style.alignItems = 'center';
    lbl.style.gap = '8px';
    lbl.style.cursor = 'pointer';

    const chk = document.createElement('input');
    chk.type = 'checkbox';
    chk.value = p.avatar_id;
    chk.className = 'admin-allowed-avatar-chk';
    chk.checked = selected.includes(p.avatar_id);

    lbl.appendChild(chk);
    lbl.appendChild(document.createTextNode(p.label || p.avatar_id));
    container.appendChild(lbl);
  });
}

function getCheckedAllowedAvatars(): string[] {
  return Array.from(document.querySelectorAll<HTMLInputElement>('.admin-allowed-avatar-chk:checked')).map((chk) => chk.value);
}

/** Fills the user form's «Профиль аватара» select. Показываем только профили из
 * «Доступных», чтобы админ не назначил аватар, которого пользователь не вправе
 * выбрать; пустой набор — все. */
function populateUserAvatarProfiles(selectedId: string): void {
  const sel = $<HTMLSelectElement>('admin-user-avatar-profile');
  if (!sel) return;

  const allowed = getCheckedAllowedAvatars();
  const profiles = allowed.length > 0
    ? adminAvatarProfilesCache.filter((p) => allowed.includes(p.avatar_id))
    : adminAvatarProfilesCache;

  const known = profiles.some((p) => p.avatar_id === selectedId);
  const extra = selectedId && !known
    ? `<option value="${escAttr(selectedId)}" selected>${escAttr(selectedId)} (вне доступных)</option>`
    : '';
  sel.innerHTML = `<option value="">— не задан —</option>${extra}` + profiles
    .map((p) => `<option value="${escAttr(p.avatar_id)}"${p.avatar_id === selectedId ? ' selected' : ''}>${escAttr(p.label || p.avatar_id)}</option>`)
    .join('');
  sel.value = selectedId;
}

/** Селект ← трёхзначная колонка users.dialog_og_turn. */
function setOgTurnSelect(value: boolean | null | undefined): void {
  const sel = $<HTMLSelectElement>('admin-user-og-turn');
  if (sel) sel.value = value === true ? 'graph' : value === false ? 'fsm' : '';
}

/** Селект → payload: '' = null («по серверному флагу»). */
function readOgTurnSelect(): boolean | null {
  const v = $<HTMLSelectElement>('admin-user-og-turn')?.value || '';
  return v === 'graph' ? true : v === 'fsm' ? false : null;
}

/** Подсказка с ДЕЙСТВУЮЩИМ дефолтом стенда: без OG-контура выбор «граф»
 * ничего не меняет — админ должен видеть это прямо в форме, а не догадываться
 * по поведению Диалога. Сбой запроса не прячет форму — остаётся базовый текст. */
async function renderOgTurnHint(): Promise<void> {
  const hint = $('admin-user-og-turn-hint');
  if (!hint) return;
  try {
    const r = await fetch('/api/admin/avg-dialog/og-status', {
      headers: { Authorization: 'Bearer ' + state.authToken },
    });
    if (!r.ok) return;
    const s = await r.json() as { og_enabled?: boolean; env_default_graph?: boolean };
    if (!s.og_enabled) {
      hint.textContent = '⚠️ OG-контур выключен (OG_ENABLED) — все ходы идут по FSM, выбор «граф» не действует.';
      return;
    }
    hint.textContent = `Серверный дефолт сейчас: ${s.env_default_graph ? 'граф оркестрации' : 'FSM'}. `
      + 'Граф переживает падение процесса (ход долечивается recovery), FSM — классический путь.';
  } catch {
    // Non-fatal: остаётся статический текст подсказки.
  }
}

/** Ключи пользователя в карточке (IL-1 1.5): администратор видит и отзывает;
 * формы выпуска нет — выпуск от чужого имени не предусмотрен (IL-ADR-1 §I2). */
async function loadAdminUserApiKeys(username: string): Promise<void> {
  const section = $('admin-user-api-keys-section');
  const list = $('admin-user-api-keys-list');
  if (!section || !list) return;
  if (!username) {
    // Новый пользователь: учётной записи ещё нет — ключей быть не может.
    section.classList.add('hidden');
    return;
  }
  section.classList.remove('hidden');
  list.textContent = 'Загрузка ключей…';
  try {
    const r = await fetch(`/api/admin/users/${encodeURIComponent(username)}/api-keys`, {
      headers: { Authorization: 'Bearer ' + state.authToken },
    });
    if (!r.ok) {
      list.textContent = 'Не удалось загрузить ключи';
      return;
    }
    const d = await r.json() as { keys?: ApiKeyView[] };
    list.textContent = '';
    if (!d.keys?.length) {
      const empty = document.createElement('div');
      empty.className = 'user-docs-empty';
      empty.textContent = 'Ключей нет.';
      list.appendChild(empty);
      return;
    }
    for (const key of d.keys) {
      list.appendChild(buildApiKeyRow(key, (k) => { void revokeAdminUserApiKey(username, k); }));
    }
  } catch {
    list.textContent = 'Не удалось загрузить ключи';
  }
}

async function revokeAdminUserApiKey(username: string, key: ApiKeyView): Promise<void> {
  if (!confirm(
    `Отозвать ключ «${key.name}» пользователя ${username}?\n\n`
    + 'Программы, использующие его, сразу перестанут работать. Отзыв необратим.'
  )) return;
  try {
    const r = await fetch(
      `/api/admin/users/${encodeURIComponent(username)}/api-keys/${key.id}`,
      { method: 'DELETE', headers: { Authorization: 'Bearer ' + state.authToken } },
    );
    if (r.ok) {
      showToast('✅ Ключ отозван');
    } else {
      const err = await r.json().catch(() => ({})) as ApiErrorBody;
      showToast('❌ ' + formatApiErrors(err, 'Не удалось отозвать ключ'));
    }
  } catch (e) {
    showToast('❌ Ошибка сети: ' + (e instanceof Error ? e.message : String(e)));
  }
  void loadAdminUserApiKeys(username);
}

let allowedAvatarSyncBound = false;

/** Снятие галочки в «Доступных» должно сразу сузить селект назначенного
 * профиля — иначе админ сохранил бы аватар вне разрешённого набора.
 * Слушатель делегирован на контейнер (он переживает перерисовку чекбоксов),
 * поэтому вешаем его ровно один раз. */
function bindAllowedAvatarSync(): void {
  if (allowedAvatarSyncBound) return;
  const container = $('admin-allowed-avatars-container');
  if (!container) return;
  allowedAvatarSyncBound = true;
  container.addEventListener('change', (e) => {
    if (!(e.target as Element)?.classList?.contains('admin-allowed-avatar-chk')) return;
    const sel = $<HTMLSelectElement>('admin-user-avatar-profile');
    populateUserAvatarProfiles(sel?.value || '');
  });
}

async function editAdminUser(username: string, usersMap: Record<string, AdminUserRecord>) {
  const sourceUsers = Object.keys(usersMap || {}).length > 0 ? usersMap : adminUsersCache;
  const templateUser = !username ? sourceUsers?.user_a || null : null;
  const user = username && sourceUsers[username] ? sourceUsers[username] : templateUser;
  const selectedCategories = user?.allowed_categories || [];
  const categoriesContainer = $('admin-allowed-categories-container');
  if (categoriesContainer) categoriesContainer.textContent = 'Загрузка категорий...';

  try {
    const cats = await getAdminCategories();
    renderAllowedCategories(cats, selectedCategories, !user);
    bindAllowedCategorySync(user?.category || '');
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Не удалось загрузить категории';
    if (categoriesContainer) categoriesContainer.textContent = msg;
    showToast('❌ ' + msg);
  }
  try {
    adminAvatarProfilesCache = await fetchAdminAvatarProfiles();
    renderAllowedAvatars(
      adminAvatarProfilesCache,
      (user?.allowed_dialog_avatars as string[] | undefined) || []
    );
    bindAllowedAvatarSync();
    populateUserAvatarProfiles((user?.dialog_avatar_id as string | null) || '');
  } catch {
    // Non-fatal: avatar-profile list unavailable.
  }
  setOgTurnSelect(user?.dialog_og_turn);
  void renderOgTurnHint();
  void loadAdminUserApiKeys(username);

  $('admin-edit-card')?.classList.remove('hidden');
  const titleEl = $('admin-edit-title');
  if (titleEl) titleEl.textContent = username ? 'Редактировать ' + username : 'Новый пользователь';

  const usernameEl = $<HTMLInputElement>('admin-username');
  if (usernameEl) {
    usernameEl.value = username || '';
    usernameEl.disabled = !!username;
  }
  const emailEl = $<HTMLInputElement>('admin-email');
  if (emailEl) emailEl.value = username ? user?.email || '' : '';
  const passwordEl = $<HTMLInputElement>('admin-password');
  if (passwordEl) passwordEl.value = '';

  if (user) {
    const u = user;
    const isAdminEl = $<HTMLInputElement>('admin-is-admin');
    if (isAdminEl) isAdminEl.checked = !!u.is_admin;
    const isBlockedEl = $<HTMLInputElement>('admin-is-blocked');
    if (isBlockedEl) isBlockedEl.checked = !!u.is_blocked;
    const expirationEl = $<HTMLInputElement>('admin-expiration');
    if (expirationEl) expirationEl.value = u.expiration_date || '';
    const sysPromptEl = $<HTMLTextAreaElement>('admin-system-prompt');
    if (sysPromptEl) sysPromptEl.value = u.system_prompt || '';
    const showPromptEl = $<HTMLInputElement>('admin-show-prompt-to-user');
    if (showPromptEl) showPromptEl.checked = !!u.show_prompt_to_user;

    const uiModules = getUserUiModules(u);
    const trajModuleEl = $<HTMLInputElement>('admin-ui-module-journey');
    if (trajModuleEl) trajModuleEl.checked = uiModules.journey;
    const mapsModuleEl = $<HTMLInputElement>('admin-ui-module-examples');
    if (mapsModuleEl) mapsModuleEl.checked = uiModules.examples;

    setAdminBillingFields(u);
  } else {
    const isAdminEl = $<HTMLInputElement>('admin-is-admin');
    if (isAdminEl) isAdminEl.checked = false;
    const isBlockedEl = $<HTMLInputElement>('admin-is-blocked');
    if (isBlockedEl) isBlockedEl.checked = false;
    const expirationEl = $<HTMLInputElement>('admin-expiration');
    if (expirationEl) expirationEl.value = '2099-12-31';
    const sysPromptEl = $<HTMLTextAreaElement>('admin-system-prompt');
    if (sysPromptEl) sysPromptEl.value = 'Ты — полезный ИИ-ассистент Gemma 4. Отвечай точно и по существу.';
    const showPromptEl = $<HTMLInputElement>('admin-show-prompt-to-user');
    if (showPromptEl) showPromptEl.checked = false;
    const trajModuleEl = $<HTMLInputElement>('admin-ui-module-journey');
    if (trajModuleEl) trajModuleEl.checked = true;
    const mapsModuleEl = $<HTMLInputElement>('admin-ui-module-examples');
    if (mapsModuleEl) mapsModuleEl.checked = false;
    setAdminBillingFields({ balance_usd: 0, credit_limit_usd: 0, cost_usd_used: 0 });
  }
  $('admin-edit-card')?.scrollIntoView({ behavior: 'smooth' });
}

$('btn-create-user')?.addEventListener('click', (e: MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
  editAdminUser('', adminUsersCache);
});
$('btn-cancel-user')?.addEventListener('click', (e: MouseEvent) => {
  e.preventDefault();
  $('admin-edit-card')?.classList.add('hidden');
});

$('btn-save-user')?.addEventListener('click', async (e: MouseEvent) => {
  e.preventDefault();
  const username = $<HTMLInputElement>('admin-username')?.value.trim() || '';
  if (!username) return showToast('❌ Имя обязательно');

  if (username.length < 3 || username.length > 64 || !USERNAME_RE.test(username)) {
    return showToast('❌ Имя должно быть от 3 до 64 символов (только a-z, A-Z, 0-9, _ и -)');
  }

  const allowed_categories = getCheckedAllowedCategories();
  if (allowed_categories.length === 0) {
    return showToast('❌ Выберите хотя бы одну доступную категорию');
  }
  const defaultCategory = $<HTMLSelectElement>('admin-default-category')?.value || allowed_categories[0];
  if (!allowed_categories.includes(defaultCategory)) {
    return showToast('❌ Категория по умолчанию должна быть среди доступных категорий');
  }

  const is_admin = $<HTMLInputElement>('admin-is-admin')?.checked;
  const is_blocked = $<HTMLInputElement>('admin-is-blocked')?.checked;
  const billing = readAdminBillingPayload();
  const show_prompt_to_user = $<HTMLInputElement>('admin-show-prompt-to-user')?.checked || false;
  const ui_modules = {
    journey: $<HTMLInputElement>('admin-ui-module-journey')?.checked !== false,
    examples: $<HTMLInputElement>('admin-ui-module-examples')?.checked === true,
  };

  const payload: Record<string, unknown> = {
    email: $<HTMLInputElement>('admin-email')?.value.trim() || null,
    is_admin: is_admin,
    is_blocked: is_blocked,
    balance_usd: billing.balance_usd,
    credit_limit_usd: billing.credit_limit_usd,
    expiration_date: $<HTMLInputElement>('admin-expiration')?.value || null,
    system_prompt: $<HTMLTextAreaElement>('admin-system-prompt')?.value || null,
    show_prompt_to_user: show_prompt_to_user,
    ui_modules,
    category: defaultCategory,
    dialog_avatar_id: $<HTMLSelectElement>('admin-user-avatar-profile')?.value || null,
    dialog_og_turn: readOgTurnSelect(),
    allowed_dialog_avatars: getCheckedAllowedAvatars(),
    allowed_categories: allowed_categories
  };

  const p = $<HTMLInputElement>('admin-password')?.value || '';
  if (p) {
    const passwordErrors = getPasswordErrors(p);
    if (passwordErrors.length > 0) return showToast('❌ ' + passwordErrors.join('; '));
    payload.password = p;
  }

  try {
    const r = await fetch('/api/admin/users/' + encodeURIComponent(username), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + state.authToken },
      body: JSON.stringify(payload)
    });

    if (r.ok) {
      showToast('✅ Пользователь сохранен');
      $('admin-edit-card')?.classList.add('hidden');
      loadAdminUsers();
    } else {
      const errData = await r.json() as ApiErrorBody;
      console.error('[Admin] Save user failed:', errData);
      showToast('❌ Ошибка: ' + formatApiErrors(errData, 'Не удалось сохранить пользователя'));
    }
  } catch (err) {
    console.error('[Admin] Network error during save:', err);
    showToast('❌ Ошибка сети: ' + (err instanceof Error ? err.message : String(err)));
  }
});

/** Корзина = деактивация: вход закрывается сразу, строки в БД живут до purge.
 * Сообщаем именно это — раньше тост обещал удаление, а пользователь оставался
 * в списке, и админ жал корзину снова и снова. */
async function deleteAdminUser(username: string) {
  if (!confirm(
    `Деактивировать ${username}?\n\n`
    + 'Вход закроется сразу, данные будут удалены по истечении grace-периода.\n'
    + 'Чтобы стереть пользователя прямо сейчас, используйте «Удалить окончательно» (⛔).'
  )) return;
  const r = await fetch('/api/admin/users/' + encodeURIComponent(username), {
    method: 'DELETE',
    headers: { 'Authorization': 'Bearer ' + state.authToken }
  });
  if (r.ok) {
    const data = await r.json().catch(() => ({})) as { purge_scheduled_at?: number };
    const purgeDate = formatPurgeDate(data.purge_scheduled_at);
    showToast(purgeDate
      ? `🕒 Пользователь деактивирован, удаление ${purgeDate}`
      : '🕒 Пользователь деактивирован');
    loadAdminUsers();
  } else {
    const err = await r.json().catch(() => ({})) as ApiErrorBody;
    showToast('❌ ' + formatApiErrors(err, 'Не удалось деактивировать пользователя'));
  }
}

async function purgeAdminUser(username: string) {
  if (!confirm(
    `Удалить ${username} окончательно?\n\n`
    + 'Вместе с учётной записью каскадом уйдут её разговоры, траектории, документы\n'
    + 'и история платежей. Восстановить их будет нельзя.'
  )) return;
  const r = await fetch('/api/admin/users/' + encodeURIComponent(username) + '/purge', {
    method: 'DELETE',
    headers: { 'Authorization': 'Bearer ' + state.authToken }
  });
  if (r.ok) {
    showToast('🗑️ Пользователь удалён');
    loadAdminUsers();
  } else {
    const err = await r.json().catch(() => ({})) as ApiErrorBody;
    showToast('❌ ' + formatApiErrors(err, 'Не удалось удалить пользователя'));
  }
}
